// scenes/.../scan.ts

import SceneObject from "../../../lib/services/scene/sceneobject";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import Camera from "../../../lib/services/scene/camera";
import Stage from "../../../lib/services/stage/stage";
import ScanRenderPipeline from "./scan.renderpipeline"
import CaptureService from "../../../lib/services/capture/capture_service";


/**
 * Paper-craft scan frame composed of four corner pieces anchored to the
 * corners of the video rectangle. Each corner is its own quad with its
 * own texture, sized at a fraction of the rectangle's dimensions.
 */
export default class Scan extends SceneObject {
  private pipeline_tl: ScanRenderPipeline;
  private pipeline_tr: ScanRenderPipeline;
  private pipeline_bl: ScanRenderPipeline;
  private pipeline_br: ScanRenderPipeline;
  private capture_service:CaptureService;

  private active: boolean = false;
  // Native image aspect ratios (image_width / image_height).
// Measure these from the actual PNGs. Defaults below assume the top
// corners are landscape-ish and the bottom corners are portrait-ish.
    private readonly IMAGE_ASPECT_TL = 480 / 320;   // top-left: wide-ish
    private readonly IMAGE_ASPECT_TR = 480 / 320;   // top-right: same
    private readonly IMAGE_ASPECT_BL = 480 / 640;   // bottom-left: tall (treehouse)
    private readonly IMAGE_ASPECT_BR = 480 / 480;   // bottom-right: square-ish (doghouse)

  constructor(key: string) {
    super(key);
    this.pipeline_tl = new ScanRenderPipeline("scan_tl", "scan_corner", "scan_tl");
    this.pipeline_tr = new ScanRenderPipeline("scan_tr", "scan_corner", "scan_tr");
    this.pipeline_bl = new ScanRenderPipeline("scan_bl", "scan_corner", "scan_bl");
    this.pipeline_br = new ScanRenderPipeline("scan_br", "scan_corner", "scan_br");
    this.layer = 1; // above video
    this.capture_service = Stage.instance().capture_service;
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    await this.pipeline_tl.build(device, format, shader_service, resource_service);
    await this.pipeline_tr.build(device, format, shader_service, resource_service);
    await this.pipeline_bl.build(device, format, shader_service, resource_service);
    await this.pipeline_br.build(device, format, shader_service, resource_service);
  }

  override ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);
    if (cmd === "scan_sticker") {
      this.active = !this.active;
    }
  }

  override tick(_delta_time: number): void {}

   private detect_iphone(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPod/.test(ua);
}

  override prepare(device: GPUDevice, camera: Camera): void {
  if (!this.active) return;

  const canvas = Stage.instance().render_service.canvas;
  if (!canvas) return;
  const is_iphone = this.detect_iphone();

  const src_w = this.capture_service.camera_info.y_width;   // 1280
  const src_h = this.capture_service.camera_info.y_height;  // 720
   const rot = is_iphone; // rear cam rotated 90 on iPhone

  // Match the video shader's aspect-fit math.
  const eff_w = rot ? src_h : src_w;
  const eff_h = rot ? src_w : src_h;

  const src_aspect = eff_w / eff_h;
  const dst_aspect = canvas.width / canvas.height;

  let hx: number;
  let hy: number;
  if (src_aspect > dst_aspect) {
    hx = 1.0;
    hy = dst_aspect / src_aspect;
  } else {
    hx = src_aspect / dst_aspect;
    hy = 1.0;
  }

  // Video rectangle in NDC.
  const video_w_ndc = 2 * hx;
  const video_h_ndc = 2 * hy;

  // Width of each corner in NDC = 25% of video width.
  //const CORNER_WIDTH_FRACTION = 0.25;

  const CORNER_WIDTH_FRACTION = is_iphone ? 0.18 : 0.25;
  const corner_w_ndc = video_w_ndc * CORNER_WIDTH_FRACTION;

  // Each corner's NDC width is fixed; its NDC height comes from the
  // image's native aspect, then scaled to NDC-y space using the canvas
  // aspect so the quad covers the right number of pixels.
  //
  // image_aspect = image_width_px / image_height_px
  // corner_w_pixels = corner_w_ndc * (canvas_w / 2)
  // corner_h_pixels = corner_w_pixels / image_aspect
  // corner_h_ndc    = corner_h_pixels / (canvas_h / 2)
  //                 = corner_w_ndc * (canvas_w / canvas_h) / image_aspect
  //
  // i.e. height_ndc = width_ndc * (canvas_aspect / image_aspect)
  const canvas_aspect = canvas.width / canvas.height;

  const height_for = (image_aspect: number) =>
    corner_w_ndc * (canvas_aspect / image_aspect);

  // Top-left
  const tl_h = height_for(this.IMAGE_ASPECT_TL);
  this.pipeline_tl.update_params(
    device,
    -hx,
    hy,
    -hx + corner_w_ndc,
    hy - tl_h
  );

  // Top-right
  const tr_h = height_for(this.IMAGE_ASPECT_TR);
  this.pipeline_tr.update_params(
    device,
    hx - corner_w_ndc,
    hy,
    hx,
    hy - tr_h
  );

  // Bottom-left
  const bl_h = height_for(this.IMAGE_ASPECT_BL);
  this.pipeline_bl.update_params(
    device,
    -hx,
    -hy + bl_h,
    -hx + corner_w_ndc,
    -hy
  );

  // Bottom-right
  const br_h = height_for(this.IMAGE_ASPECT_BR);
  this.pipeline_br.update_params(
    device,
    hx - corner_w_ndc,
    -hy + br_h,
    hx,
    -hy
  );

  this.pipeline_tl.prepare(device);
  this.pipeline_tr.prepare(device);
  this.pipeline_bl.prepare(device);
  this.pipeline_br.prepare(device);
}


  override draw(pass: GPURenderPassEncoder): void {
    if (!this.active) return;
    this.pipeline_tl.draw(pass);
    this.pipeline_tr.draw(pass);
    this.pipeline_bl.draw(pass);
    this.pipeline_br.draw(pass);
  }
}